import React, { useState, useRef, useCallback } from 'react';
import {
  XMarkIcon,
  SparklesIcon,
  PhotoIcon,
  ClockIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import {
  Platform,
  SocialPost,
  SocialStatus,
  createPosts,
  uploadImage,
  deleteImage,
  generateAIDraft,
  listPosts,
} from '../services/socialMediaService';

const EF_DOMAINS = [
  'task initiation',
  'working memory',
  'time management',
  'emotional regulation',
  'organization',
  'prioritization',
  'sustained attention',
  'cognitive flexibility',
];

const PLATFORM_LABELS: Record<Platform, string> = {
  linkedin: 'LinkedIn',
  twitter: 'Twitter/X',
  facebook: 'Facebook',
  instagram: 'Instagram',
};

const PLATFORM_CHAR_LIMITS: Record<Platform, number> = {
  linkedin: 1300,
  twitter: 280,
  facebook: 500,
  instagram: 2200,
};

const PLATFORM_COLORS: Record<Platform, string> = {
  linkedin: 'bg-blue-600',
  twitter: 'bg-black',
  facebook: 'bg-blue-500',
  instagram: 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400',
};

interface Props {
  open: boolean;
  onClose: () => void;
  socialStatuses: Record<Platform, SocialStatus | null>;
}

type PostResult = { platform: Platform; post: SocialPost };

const SocialMediaPostModal: React.FC<Props> = ({ open, onClose, socialStatuses }) => {
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<Platform>>(new Set());
  const [content, setContent] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadedImage, setUploadedImage] = useState<{ r2_key: string; image_url: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [timing, setTiming] = useState<'now' | 'schedule'>('now');
  const [scheduledAt, setScheduledAt] = useState('');

  // AI draft
  const [showAI, setShowAI] = useState(false);
  const [aiTopic, setAITopic] = useState('');
  const [aiDomain, setAIDomain] = useState('');
  const [aiFreeText, setAIFreeText] = useState('');
  const [aiLoading, setAILoading] = useState(false);
  const [aiError, setAIError] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<PostResult[] | null>(null);
  const [submitError, setSubmitError] = useState('');

  // Post history
  const [history, setHistory] = useState<SocialPost[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<Platform | ''>('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compute effective char limit
  const effectiveLimit = selectedPlatforms.size > 0
    ? Math.min(...Array.from(selectedPlatforms).map(p => PLATFORM_CHAR_LIMITS[p]))
    : 1300;

  const connectedPlatforms = (Object.keys(socialStatuses) as Platform[]).filter(
    p => socialStatuses[p]?.connected
  );

  const instagramSelected = selectedPlatforms.has('instagram');
  const canSubmit =
    selectedPlatforms.size > 0 &&
    content.trim().length > 0 &&
    content.length <= effectiveLimit &&
    (!instagramSelected || uploadedImage !== null) &&
    (timing === 'now' || scheduledAt !== '');

  const togglePlatform = (p: Platform) => {
    setSelectedPlatforms(prev => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const selectAll = () => setSelectedPlatforms(new Set(connectedPlatforms));
  const clearAll = () => setSelectedPlatforms(new Set());

  // Image handling
  const handleImageDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) await processImageFile(file);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await processImageFile(file);
  };

  const processImageFile = async (file: File) => {
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);
    try {
      const result = await uploadImage(file);
      setUploadedImage(result);
    } catch (err: any) {
      setSubmitError(`Image upload failed: ${err.message}`);
      setImageFile(null);
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = async () => {
    if (uploadedImage) {
      try { await deleteImage(uploadedImage.r2_key); } catch {}
    }
    setImageFile(null);
    setImagePreview(null);
    setUploadedImage(null);
  };

  // AI draft
  const handleGenerateDraft = async () => {
    if (selectedPlatforms.size === 0) {
      setAIError('Select at least one platform first');
      return;
    }
    setAILoading(true);
    setAIError('');
    const platform = Array.from(selectedPlatforms)[0];
    try {
      const result = await generateAIDraft({
        platform,
        topic: aiTopic || undefined,
        ef_domain: aiDomain || undefined,
        free_text: aiFreeText || undefined,
      });
      setContent(result.draft);
      setShowAI(false);
    } catch (err: any) {
      setAIError(err.message);
    } finally {
      setAILoading(false);
    }
  };

  // Submit
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const posts = await createPosts({
        platforms: Array.from(selectedPlatforms),
        content,
        image_url: uploadedImage?.image_url,
        image_r2_key: uploadedImage?.r2_key,
        scheduled_at: timing === 'schedule' && scheduledAt ? scheduledAt : undefined,
        ai_generated: false,
      });
      setResults(posts.map(p => ({ platform: p.platform as Platform, post: p })));
    } catch (err: any) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Load history
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const posts = await listPosts(historyFilter || undefined);
      setHistory(posts);
    } catch {}
    setHistoryLoading(false);
  };

  const handleClose = async () => {
    // Clean up orphaned uploaded image if not posted
    if (uploadedImage && results === null) {
      try { await deleteImage(uploadedImage.r2_key); } catch {}
    }
    // Reset state
    setSelectedPlatforms(new Set());
    setContent('');
    setImageFile(null);
    setImagePreview(null);
    setUploadedImage(null);
    setTiming('now');
    setScheduledAt('');
    setResults(null);
    setSubmitError('');
    setShowAI(false);
    setAITopic('');
    setAIDomain('');
    setAIFreeText('');
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Create Social Post</h2>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {results ? (
            /* Results view */
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Post Results</h3>
              {results.map(({ platform, post }) => (
                <div key={platform} className={`flex items-center gap-3 p-4 rounded-xl border ${
                  post.status === 'published' ? 'border-green-200 bg-green-50' :
                  post.status === 'scheduled' ? 'border-blue-200 bg-blue-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${
                    post.status === 'published' ? 'bg-green-500' :
                    post.status === 'scheduled' ? 'bg-blue-500' :
                    'bg-red-500'
                  }`} />
                  <div className="flex-1">
                    <p className="font-medium text-sm text-gray-900">{PLATFORM_LABELS[platform]}</p>
                    <p className="text-xs text-gray-500 capitalize">{post.status}
                      {post.error_message && ` — ${post.error_message}`}
                    </p>
                  </div>
                </div>
              ))}
              <button
                onClick={handleClose}
                className="w-full py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Platform selector */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">Post to</label>
                  <div className="flex gap-2 text-xs">
                    <button onClick={selectAll} className="text-indigo-600 hover:underline">Select all</button>
                    <span className="text-gray-300">|</span>
                    <button onClick={clearAll} className="text-gray-500 hover:underline">Clear</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(['linkedin', 'twitter', 'facebook', 'instagram'] as Platform[]).map(p => {
                    const status = socialStatuses[p];
                    const connected = status?.connected;
                    const selected = selectedPlatforms.has(p);
                    return (
                      <button
                        key={p}
                        onClick={() => connected && togglePlatform(p)}
                        disabled={!connected}
                        className={`flex items-center gap-2 p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          !connected ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' :
                          selected ? 'border-indigo-500 bg-indigo-50 text-indigo-700' :
                          'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <span className={`w-3 h-3 rounded-full ${connected ? PLATFORM_COLORS[p] : 'bg-gray-300'}`} />
                        {PLATFORM_LABELS[p]}
                        {!connected && <span className="ml-auto text-xs text-gray-400">Not connected</span>}
                        {connected && status?.handle && <span className="ml-auto text-xs text-gray-400 truncate max-w-[80px]">{status.handle}</span>}
                      </button>
                    );
                  })}
                </div>
                {instagramSelected && !uploadedImage && (
                  <p className="mt-2 text-xs text-orange-600">Instagram requires an image.</p>
                )}
              </div>

              {/* Content */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Content</label>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-mono ${
                      content.length > effectiveLimit ? 'text-red-600 font-bold' :
                      content.length > effectiveLimit * 0.9 ? 'text-orange-500' :
                      'text-gray-400'
                    }`}>{content.length}/{effectiveLimit}</span>
                    <button
                      onClick={() => setShowAI(v => !v)}
                      className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      <SparklesIcon className="w-3.5 h-3.5" />
                      Generate
                    </button>
                  </div>
                </div>
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  rows={5}
                  placeholder="Write your post..."
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm resize-none focus:outline-none focus:ring-2 transition-colors ${
                    content.length > effectiveLimit
                      ? 'border-red-300 focus:ring-red-200'
                      : 'border-gray-200 focus:ring-indigo-200'
                  }`}
                />
              </div>

              {/* AI section */}
              {showAI && (
                <div className="bg-indigo-50 rounded-xl p-4 space-y-3">
                  <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Draft Generator</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Topic (optional)</label>
                      <input
                        value={aiTopic}
                        onChange={e => setAITopic(e.target.value)}
                        placeholder="e.g. morning routines"
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">EF Domain (optional)</label>
                      <select
                        value={aiDomain}
                        onChange={e => setAIDomain(e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      >
                        <option value="">Any domain</option>
                        {EF_DOMAINS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Free text (optional)</label>
                    <input
                      value={aiFreeText}
                      onChange={e => setAIFreeText(e.target.value)}
                      placeholder="Any specific angle or message..."
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                  </div>
                  {aiError && <p className="text-xs text-red-600">{aiError}</p>}
                  <button
                    onClick={handleGenerateDraft}
                    disabled={aiLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    <SparklesIcon className="w-4 h-4" />
                    {aiLoading ? 'Generating…' : 'Generate Draft'}
                  </button>
                </div>
              )}

              {/* Image upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image {instagramSelected ? <span className="text-orange-600">(required for Instagram)</span> : <span className="text-gray-400">(optional)</span>}
                </label>
                {imagePreview ? (
                  <div className="relative inline-block">
                    <img src={imagePreview} alt="Preview" className="h-32 w-auto rounded-xl object-cover border border-gray-200" />
                    {uploadingImage && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-xl">
                        <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    <button
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div
                    onDrop={handleImageDrop}
                    onDragOver={e => e.preventDefault()}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors"
                  >
                    <PhotoIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Drag &amp; drop or <span className="text-indigo-600 font-medium">browse</span></p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 50MB</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {/* Timing */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">When to post</label>
                <div className="flex gap-2 mb-3">
                  {(['now', 'schedule'] as const).map(t => (
                    <button
                      key={t}
                      onClick={() => setTiming(t)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
                        timing === t
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {t === 'now' ? <PaperAirplaneIcon className="w-4 h-4" /> : <ClockIcon className="w-4 h-4" />}
                      {t === 'now' ? 'Post now' : 'Schedule'}
                    </button>
                  ))}
                </div>
                {timing === 'schedule' && (
                  <input
                    type="datetime-local"
                    value={scheduledAt ? scheduledAt.slice(0, 16) : ''}
                    onChange={e => setScheduledAt(e.target.value ? new Date(e.target.value).toISOString() : '')}
                    min={new Date().toISOString().slice(0, 16)}
                    className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                )}
              </div>

              {submitError && (
                <p className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{submitError}</p>
              )}
            </>
          )}

          {/* Post history */}
          {results === null && (
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Post History</h3>
                <div className="flex items-center gap-2">
                  <select
                    value={historyFilter}
                    onChange={e => setHistoryFilter(e.target.value as Platform | '')}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none"
                  >
                    <option value="">All platforms</option>
                    {(['linkedin', 'twitter', 'facebook', 'instagram'] as Platform[]).map(p => (
                      <option key={p} value={p}>{PLATFORM_LABELS[p]}</option>
                    ))}
                  </select>
                  <button
                    onClick={loadHistory}
                    className="text-xs text-indigo-600 hover:underline"
                  >
                    Load
                  </button>
                </div>
              </div>
              {historyLoading ? (
                <p className="text-xs text-gray-400">Loading…</p>
              ) : history.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {history.map(p => (
                    <div key={p.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50 text-xs">
                      <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                        p.status === 'published' ? 'bg-green-500' :
                        p.status === 'scheduled' ? 'bg-blue-500' :
                        p.status === 'failed' ? 'bg-red-500' :
                        'bg-gray-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="font-medium text-gray-700 capitalize">{p.platform}</span>
                          <span className="text-gray-400">·</span>
                          <span className="text-gray-500 capitalize">{p.status}</span>
                          {p.created_at && (
                            <>
                              <span className="text-gray-400">·</span>
                              <span className="text-gray-400">{new Date(p.created_at).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                        <p className="text-gray-600 truncate">{p.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No posts yet. Click Load to check.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {results === null && (
          <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {timing === 'now' ? 'Post Now' : 'Schedule Post'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialMediaPostModal;
